import { PASSWORD_MIN_LENGTH as MIN_LENGTH } from '@remote-medium/shared';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { AuthLayout } from './AuthLayout.tsx';
import { Button } from './components/button.tsx';
import { Field, FieldLabel } from './components/field.tsx';
import { PasswordInput } from './PasswordInput.tsx';
import { PasswordStrength } from './PasswordStrength.tsx';

// The same token flow serves two links: a reset ("forgot password") and a
// curator invite, whose first password accepts the invite. Only the words and
// the way out of a dead link differ: an invitee can't request a new one.
const COPY = {
  reset: {
    incompleteTitle: 'This link is incomplete',
    incompleteIntro: 'Open the reset link from your email again, or request a new one.',
    title: 'Choose a new password',
    intro: 'You will be signed out everywhere and asked to sign in again.',
    passwordLabel: 'New password',
    confirmLabel: 'Confirm new password',
    expired: 'This link has expired or was already used.',
    failed: 'Could not reset your password.',
    submit: 'Update password',
    done: 'Password updated. Sign in with your new password.',
  },
  invite: {
    incompleteTitle: 'This invite link is incomplete',
    incompleteIntro:
      'Open the link from your invite email again. If it still fails, ask a curator to resend your invite.',
    title: 'Set your password',
    intro: 'Choose a password to finish setting up your curator account.',
    passwordLabel: 'Password',
    confirmLabel: 'Confirm password',
    expired: 'This invite has expired or was already used. Ask a curator to resend it.',
    failed: 'Could not set your password.',
    submit: 'Set password',
    done: 'Password set. Sign in to get started.',
  },
} as const;

// Reached from the emailed link. Submitting is an explicit action, so mail
// scanners that prefetch the link can't burn the one-time token.
export function ResetPasswordPage({
  siteUrl,
  variant = 'reset',
  loginTo = '/login',
  forgotPasswordTo = '/forgot-password',
  resetPassword,
}: {
  siteUrl: string;
  variant?: keyof typeof COPY;
  loginTo?: string;
  forgotPasswordTo?: string;
  resetPassword: (
    newPassword: string,
    token: string,
  ) => Promise<{ error?: { code?: string | undefined; message?: string | undefined } | null }>;
}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;
  const copy = COPY[variant];
  // A reset can be requested again; an invite only by another curator.
  const canRequestLink = variant === 'reset';

  async function submit(e: React.SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) return;
    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    setExpired(false);
    try {
      const { error: err } = await resetPassword(password, token);
      if (err) {
        const isExpired = err.code === 'INVALID_TOKEN';
        setExpired(isExpired);
        setError(isExpired ? copy.expired : (err.message ?? copy.failed));
        setBusy(false);
        return;
      }
    } catch {
      setError('We could not reach the server. Check your connection and try again.');
      setBusy(false);
      return;
    }
    void navigate(loginTo, {
      replace: true,
      state: { notice: copy.done },
    });
  }

  const back = (
    <Link to={loginTo} className="text-primary underline-offset-4 hover:underline">
      Back to sign in
    </Link>
  );

  if (!token) {
    return (
      <AuthLayout
        siteUrl={siteUrl}
        title={copy.incompleteTitle}
        intro={copy.incompleteIntro}
        footer={back}
      >
        {canRequestLink && (
          <Link
            to={forgotPasswordTo}
            className="text-base text-primary underline-offset-4 hover:underline"
          >
            Request a new link
          </Link>
        )}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout siteUrl={siteUrl} title={copy.title} intro={copy.intro} footer={back}>
      <form className="grid gap-4" onSubmit={submit}>
        <Field>
          <FieldLabel htmlFor="password">{`${copy.passwordLabel} (at least ${MIN_LENGTH} characters)`}</FieldLabel>
          <PasswordInput
            size="lg"
            id="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordStrength password={password} />
        </Field>
        <Field>
          <FieldLabel htmlFor="confirm">{copy.confirmLabel}</FieldLabel>
          <PasswordInput
            size="lg"
            id="confirm"
            autoComplete="new-password"
            required
            value={confirm}
            aria-invalid={mismatch || undefined}
            aria-describedby={mismatch ? 'confirm-mismatch' : undefined}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <p
            id="confirm-mismatch"
            role="status"
            className={mismatch ? 'm-0 text-xs text-destructive' : 'sr-only'}
          >
            {mismatch ? 'The two passwords do not match.' : ''}
          </p>
        </Field>
        {error && (
          <p role="alert" className="m-0 text-sm text-destructive">
            {error}{' '}
            {expired && canRequestLink && (
              <Link to={forgotPasswordTo} className="underline">
                Request a new link
              </Link>
            )}
          </p>
        )}
        <Button type="submit" size="lg" disabled={busy}>
          {busy ? 'Saving…' : copy.submit}
        </Button>
      </form>
    </AuthLayout>
  );
}
